
// import { PlotViewElements } from "../dynplot/dragHandler";

export default class ElemAdornView {
    // constants
    static #kHorizontal = true; // parameter to 'isHorizontal'
    static #kVertical = false;	 // parameter to 'isHorizontal'

    constructor( Dyn, elemPlayContainer ) {
        // references to other objects
        this.Dyn = Dyn;
        this.container = elemPlayContainer;
        this.dataSet = this.container.dataSet;
        this.bounds = new Dyn.mathUtil.Rect();
        this.xAxisView = null;
        this.yAxisView = null;
        this.rightMargin = null;
        this.paper = null;
        this.plotShaper = null;
        this.adornments = null;
        this.subscriptions = [];
    }

    /** undo the init of the plot view */
    destroy() {
        // Remove subscriptions
        this.Dyn.mediatorLocalRemoveAll( this.subscriptions );

        // destroy sub-objects
        this.plotShaper?.destroy(); this.plotShaper = null;
        this.xAxis?.destroy(); this.xAxis = null;	// horizontal (X) axis data
        this.yAxis?.destroy(); this.yAxis = null;	// vertical   (Y) axis data
        this.rightMargin = null;

        // destroy SVG layer in view
        this.Dyn.plotUtil.destroyPaperAndSVGObjects( this.paper );
        this.paper = null;

        // clear references
        this.container = null;
        this.dataSet = null;
        this.adornments = null;
    }

    get axisThickness() { return 0; } // not Dyn.plotUtil.kMinAxisThickness

    // initialize the visuals, like PlotView.init() but for the ElemPlayContainer
    initVisuals() {
        // create the axes, invisible but needed for adornments
        // like in PlotUtil.isDrawMode(), create invisible axes of zero thickness, no title
        let Dyn = this.Dyn;
        this.paper = this.container.paper;
        this.rightMargin = { // minimal api needed to simulate empty Dyn.RightMarginView calls by Dyn.AxisView.setCrossAxisRef()
            isColorAxis: () => { return false; }
        }
        this.xAxis = new Dyn.AxisView( ElemAdornView.#kHorizontal, this.paper.width, this.axisThickness, this.paper );
        this.yAxis = new Dyn.AxisView( ElemAdornView.#kVertical, this.axisThickness, this.paper.height, this.paper );
        this.xAxis.initVisuals( this.paper.height*Dyn.plotUtil.kAxisMaxProportion, this.handleMouseOverAxis, this.handleMouseOutAxis, this.handleMouseUpOverAxis );
        this.yAxis.initVisuals( this.paper.width*Dyn.plotUtil.kAxisMaxProportion, this.handleMouseOverAxis, this.handleMouseOutAxis, this.handleMouseUpOverAxis );
        this.xAxis.setCrossAxisRef( this.yAxis, this.rightMargin, this.container.home.iconStyler );
        this.yAxis.setCrossAxisRef( this.xAxis, this.rightMargin, this.container.home.iconStyler );

        // create a plot shaper to manage the adornments and annotations
        this.adornments = new Dyn.PlotAdornments( this.paper );
        this.plotShaper = new Dyn.PlotShaper(
            this.dataSet, this.adornments, this.bounds );
        this.plotShaper.iconStyler = this.container.home.iconStyler; // one iconStyler for all ElemPlay views
        if( this.plotShaper.plotElements.length === 0 ) {
            this.plotShaper.addElement( new Dyn.MixupElement( Dyn ) ); // add required plot element
        }

        // subscriptions for annotation changes
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:updateAdornment", ( data ) => {
            this.handleUpdatePlotAdornment( data );
        });
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:removePlotElement", ( dragData ) => {
            this.removePlot( dragData.controlID );
        });
        // Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:startViewMode", ( modeData ) => {
        //     console.log("TODO: this.startViewMode( modeData )");
        // });
        // Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:stopViewMode", ( modeData ) => {
        //     console.log("TODO: this.stopViewMode( modeData )");
        // });
    }

    // respond to a resize of the plot container, compare to PlotView.updatePlotBounds()
    resize( newRect ) {
        let Dyn = this.Dyn;
        Dyn.mathUtil.copyRect( this.bounds, newRect );

        // yAxis is at left, for vertical coordinate axis of adornaments
        this.yAxis.bounds.setRect( this.bounds.x, this.bounds.y, this.axisThickness, this.bounds.height );
        Dyn.debugging && this.yAxis.bounds.addDebugRect( this.paper, "blue" );

        // xAxis is at bottom, for horizontal coordinate axis of adornaments
        this.xAxis.bounds.setRect( this.bounds.x, minZero( this.bounds.bottom() - this.xAxis.bounds.height ), this.bounds.width, this.axisThickness );
        Dyn.debugging && this.xAxis.bounds.addDebugRect( this.paper, "green" );

        this.xAxis.updateVisuals();
        this.yAxis.updateVisuals();

        function minZero( widthOrHeight ) {
            return( Math.max( 0, widthOrHeight )); // avoid negative width or height, 0 is OK if parent view too small
        }
    }

    // updateVisuals(), compare to PlotView.updatePlotView()
    updateVisuals() {
        // for now, just show the annotations
        let Dyn = this.Dyn,
            dummyCasePositions = []; // not used;
        this.plotShaper.plotElements.forEach( ( element ) => {
            let aa = this.adornments.getAdornmentsArray( element.primaryType );
            element.calcAdornments( aa, dummyCasePositions, this.xAxis, this.yAxis, true /* noAnimation */ );
        } );
    }

    layerElements() {
        // layering
        this.plotShaper.layerAdornments();
        // layer 5-topmost: if drawing on the plot, drawModeCoverRect disables interaction with everything underneath
        // this.drawModeCoverRect.toFront();
    }

    // if we have a background click handler, it is used to deselect adornments
    // deselect() {
    //     this.plotShaper.clearSelectedAdornments();  // deselect adornments
    // }

    // compare to PlotView.addPlot()
    addPlot( newPlotElement, extraPlotElement, noAnimation ) {
        this.plotShaper.addElement( newPlotElement );
        this.updateVisuals();
    }

    /** Remove an element from the plot, update the view, and save undo step.
     *  like PlotView.removePlot() */
    removePlot( elementControlID, optionalUndoString ) {
        let Dyn = this.Dyn,
            undoAdornsViewData = this.save(),
            elementToRemove = this.plotShaper.findElement( elementControlID ),
            actionString = optionalUndoString || // caller provides action string "Undo {{action}}"
                Dyn.loc( "Remove {{action}}", {  // element provides action string "Undo Remove Mean", etc
                    action: elementToRemove?.getUndoAction({}) });

        this.plotShaper.removeElement( elementControlID ); // update the model
        this.updateVisuals(); // redraw all adornments
        this.container.undoer.saveUndoOfAdorns( 'removeAnnotation', undoAdornsViewData, actionString );
    }

    // compare to PlotView.handleUpdatePlotAdornment()
    handleUpdatePlotAdornment( data, isUndoRedo ) {
        // data: { controlID, whatChanged, optionalUndoString }
        let Dyn = this.Dyn;
            // whatChanged = { action: 'updatedPlotAdornment', adornmentPositionOnly: true, noAnimation: true };

        if( data.saveData && data.restoreData ) {
            // create an undo step; adornment should already have been changed
            let undoStep = {
                    saveData: data.saveData, // plotElement.save() data pre-change
                    restoreData: data.restoreData,   // plotElement.save() data post-change
                    actionID: 'updatedPlotAdornment',
                    actionString: data.actionString
                },
                canUpdateCallBack = data.canUpdateCallBack || (()=>false); // if callback provided, combine sequential undo steps
            this.container.undoer.saveUndoOfPlotElement( undoStep, canUpdateCallBack );
        } else if ( isUndoRedo ) {
            console.log("TODO: implement isUndoRedo for ElemAdornView.handleUpdatePlotAdornment", data );
            // Handle undo/redo: we are passed the .saveData or .restoreData, now update the adornment
            Dyn.assert( typeof data.primaryType === 'string' ); // type Dyn.EPlotControl
            let plotElement = this.plotShaper.findElement( data.primaryType );
            if( plotElement ) {
                plotElement.restore( data );
            }
        }
        if( !(data.updatePlot===false) ) { // unless set to false
            this.updateVisuals();
        }
        if( data.adornmentSelected ) {
            // notify of summary view of selection change (otherwise no summary updates if no measures changed)
            console.log("TODO: implement plot:adornmentSelected", data);
            Dyn.mediator.publish("plot:adornmentSelected",{});
        }
    }

    save() {
        // save the plot state, like PlotView.save()
        // NOTE: only the plotShaper is saved, not the axes or rightMargin, because they are not changed
        return {
            plotShaper: this.plotShaper.save()
        };
    }

    restore( data ) {
        // restore the plot state, like PlotView.restore()
        // NOTE: only the plotShaper is restored, not the axes or rightMargin, because they are not changed
        if( data && data.plotShaper ) {
            const partialPlotState = { // for compatibility with PlotView.restore()/PlotShaper.restore()
                xAxisRef:this.xAxis,
                yAxisRef:this.yAxis,
                y2AxisData:null,
                hasSavedPlotTitle: false
            }
            this.plotShaper.restore( data.plotShaper, partialPlotState );
            this.updateVisuals();
        }
    }

    restorePlotElement( saveData ) {
        // restore a plot element, with no side effects on the ElemPlayContainer
        let plotElement = this.plotShaper.findElement( saveData.primaryType );
        plotElement?.restore( saveData ); // e.g. AnnotationLayerElement.restore()
        this.updateVisuals();
    }



}
